import React, { useEffect, useState, useCallback, useContext, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Plus, Search, Building2, Mail, Phone, MapPin, Pencil, Trash2, 
  Hotel, Users, Download, Upload, FileSpreadsheet, ClipboardCheck, 
  CalendarDays, AlertTriangle, History, Send, FileText, FileSignature,
  XCircle, Clock, Calendar, CheckCircle, DollarSign, ChevronDown, 
  Loader2, RefreshCw, X
} from "lucide-react";
import { useTenant } from "../contexts/TenantContext";
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Action type labels and colors
const ACTION_TYPES = {
  ilk_temas: { label: "İlk Temas", color: "bg-gray-100 text-gray-800", icon: Phone },
  arama_yapildi: { label: "Arama Yapıldı", color: "bg-cyan-100 text-cyan-800", icon: Phone },
  teklif_gonderildi: { label: "Teklif Gönderildi", color: "bg-blue-100 text-blue-800", icon: Send },
  sozlesme_gonderildi: { label: "Sözleşme Gönderildi", color: "bg-purple-100 text-purple-800", icon: FileText },
  sozlesme_imzalandi: { label: "Sözleşme İmzalandı", color: "bg-green-100 text-green-800", icon: FileSignature },
  beklemede: { label: "Beklemede", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  reddedildi: { label: "Reddedildi", color: "bg-red-100 text-red-800", icon: XCircle },
  planlandi: { label: "Planlandı", color: "bg-indigo-100 text-indigo-800", icon: Calendar, auto: true },
  denetim_tamamlandi: { label: "Denetim Tamamlandı", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle, auto: true },
  tamamlandi: { label: "Tamamlandı", color: "bg-green-100 text-green-800", icon: CheckCircle, auto: true },
};

// Manual action types (excludes auto-generated ones)
const MANUAL_ACTION_TYPES = Object.fromEntries(
  Object.entries(ACTION_TYPES).filter(([_, val]) => !val.auto)
);

const PAYMENT_STATUS = {
  bekliyor: { label: "Bekliyor", color: "bg-yellow-100 text-yellow-800" },
  kismi_odeme: { label: "Kısmi Ödeme", color: "bg-orange-100 text-orange-800" },
  tamamlandi: { label: "Tamamlandı", color: "bg-green-100 text-green-800" },
  iptal: { label: "İptal", color: "bg-red-100 text-red-800" },
};

const Customers = () => {
  const { user } = useContext(AuthContext);
  const { tenantSlug } = useTenant();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Main state
  const [customers, setCustomers] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [assignFilter, setAssignFilter] = useState("all");
  
  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [editSalesDialogOpen, setEditSalesDialogOpen] = useState(false);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  
  // Selected & Loading states
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerLogs, setCustomerLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [activeTab, setActiveTab] = useState("basic");
  const [formData, setFormData] = useState({
    name: "", brand_name: "", company_name: "", email: "", phone: "",
    address: "", city: "", province: "", country: "", company_type: "",
    notes: "", contact_person: "", contact_person_email: "", contact_title: "",
    audit_phase: "2", audit_type: "Initial Certification",
    certificate_validity_date: "", certificate_status: "",
    certificate_issue_date: "", certificate_number: "",
    facility_name: "", facility_id: "", facility_type: "",
    room_count: "", bed_count: "", employee_count: "",
    shift_count: "", manager_count: "", other_employee_count: "",
    sustainable_manager: "", other_standards: "", organization_info: "",
    risk_level: "low_risk", manday: "", star_rating: "",
    tax_number: "", safe_tourism_id: "", planning_status: "", source: "",
    bakanlik_belge_turu: ""
  });
  
  // Action form state
  const [actionForm, setActionForm] = useState({
    action_type: '', amount: '', payment_status: '',
    payment_promise_date: '', notes: '', contact_person: '', contact_title: ''
  });
  
  // Edit sales form state
  const [editSalesForm, setEditSalesForm] = useState({
    status: '', amount: '', payment_status: '', notes: '',
    contacted_person: '', contacted_person_title: ''
  });

  // Permissions
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const isSalesRep = user?.role === 'sales_representative';
  const canPlanAudit = ['admin', 'super_admin', 'planning_expert'].includes(user?.role);
  const canManageSales = !['planning_expert'].includes(user?.role); // Planlama uzmanı satış işlemleri yapamaz

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '₺0';
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
  };

  // Load data
  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const [customersRes, statsRes, repsRes] = await Promise.all([
        axios.get(`${API}/sales/customers`, { withCredentials: true }),
        axios.get(`${API}/sales/stats`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/sales/representatives`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setCustomers(customersRes.data);
      setStats(statsRes.data);
      setSalesReps(repsRes.data);
    } catch (error) {
      toast.error("Veriler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // Export
  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/sales/export`, { withCredentials: true });
      const data = response.data;
      if (!data || data.length === 0) {
        toast.error("İndirilecek veri yok");
        return;
      }
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Müşteriler");
      XLSX.writeFile(wb, `musteriler_satis_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success("Liste indirildi");
    } catch (error) {
      toast.error("İndirme başarısız");
    }
  };

  // Download template
  const handleDownloadTemplate = async () => {
    try {
      const templateUrl = tenantSlug === 'royalcert' 
        ? `${API}/export/customers/template/royalcert`
        : `${API}/export/customers/template`;
      const response = await axios.get(templateUrl, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'musteri_sablonu.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Şablon indirildi");
    } catch (error) {
      toast.error("Şablon indirilemedi");
    }
  };

  // Import
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/import/customers`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${response.data.imported_count || 0} müşteri içe aktarıldı`);
      setImportDialogOpen(false);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İçe aktarma başarısız");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Customer CRUD
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.customer_id}`, formData, { withCredentials: true });
        toast.success("Müşteri güncellendi");
      } else {
        await axios.post(`${API}/customers`, formData, { withCredentials: true });
        toast.success("Müşteri eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg || d).join(", "));
      } else if (typeof detail === 'object') {
        toast.error(detail.msg || JSON.stringify(detail));
      } else {
        toast.error(detail || "İşlem başarısız");
      }
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name || "", brand_name: customer.brand_name || "",
      company_name: customer.company_name || "", email: customer.email || "",
      phone: customer.phone || "", address: customer.address || "",
      city: customer.city || "", province: customer.province || "",
      country: customer.country || "", company_type: customer.company_type || "",
      notes: customer.notes || "", contact_person: customer.contact_person || "",
      contact_person_email: customer.contact_person_email || "",
      contact_title: customer.contact_title || "", audit_phase: customer.audit_phase || "2",
      audit_type: customer.audit_type || "Initial Certification",
      certificate_validity_date: customer.certificate_validity_date || "",
      certificate_status: customer.certificate_status || "",
      certificate_issue_date: customer.certificate_issue_date || "",
      certificate_number: customer.certificate_number || "",
      facility_name: customer.facility_name || "", facility_id: customer.facility_id || "",
      facility_type: customer.facility_type || "", room_count: customer.room_count || "",
      bed_count: customer.bed_count || "", employee_count: customer.employee_count || "",
      shift_count: customer.shift_count || "", manager_count: customer.manager_count || "",
      other_employee_count: customer.other_employee_count || "",
      sustainable_manager: customer.sustainable_manager || "",
      other_standards: customer.other_standards || "",
      organization_info: customer.organization_info || "",
      risk_level: customer.risk_level || "low_risk", manday: customer.manday || "",
      star_rating: customer.star_rating || "", tax_number: customer.tax_number || "",
      safe_tourism_id: customer.safe_tourism_id || "",
      planning_status: customer.planning_status || "", source: customer.source || "",
      bakanlik_belge_turu: customer.bakanlik_belge_turu || ""
    });
    setActiveTab("basic");
    setDialogOpen(true);
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm("Bu müşteriyi silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/customers/${customerId}`, { withCredentials: true });
      toast.success("Müşteri silindi");
      fetchCustomers();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setFormData({
      name: "", brand_name: "", company_name: "", email: "", phone: "",
      address: "", city: "", province: "", country: "", company_type: "",
      notes: "", contact_person: "", contact_person_email: "", contact_title: "", audit_phase: "2",
      audit_type: "Initial Certification", certificate_validity_date: "",
      certificate_status: "", certificate_issue_date: "", certificate_number: "",
      facility_name: "", facility_id: "", facility_type: "", room_count: "",
      bed_count: "", employee_count: "", shift_count: "", manager_count: "",
      other_employee_count: "", sustainable_manager: "", other_standards: "",
      organization_info: "", risk_level: "low_risk", manday: "", star_rating: "",
      tax_number: "", safe_tourism_id: "", planning_status: "", source: "",
      bakanlik_belge_turu: ""
    });
    setActiveTab("basic");
  };

  // Sales Action Dialog
  const handleOpenActionDialog = (customer) => {
    setSelectedCustomer(customer);
    setActionForm({
      action_type: '', amount: customer.sales_amount || '',
      payment_status: customer.payment_status || '', payment_promise_date: '',
      notes: '', contact_person: customer.contact_person || '',
      contact_title: customer.contact_title || ''
    });
    setActionDialogOpen(true);
  };

  const handleSubmitAction = async () => {
    if (!actionForm.action_type) {
      toast.error('Lütfen bir aksiyon seçin');
      return;
    }
    try {
      await axios.post(`${API}/sales/activities`, {
        customer_id: selectedCustomer.customer_id,
        action_type: actionForm.action_type,
        amount: actionForm.amount ? parseFloat(actionForm.amount) : null,
        payment_status: actionForm.payment_status || null,
        payment_promise_date: actionForm.payment_promise_date || null,
        notes: actionForm.notes,
        contact_person: actionForm.contact_person,
        contact_title: actionForm.contact_title
      }, { withCredentials: true });
      toast.success('Aksiyon kaydedildi');
      setActionDialogOpen(false);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Aksiyon kaydedilemedi');
    }
  };

  // Edit Sales Dialog
  const handleOpenEditSalesDialog = (customer) => {
    setSelectedCustomer(customer);
    setEditSalesForm({
      status: customer.sales_status || '',
      amount: customer.sales_amount || '',
      payment_status: customer.payment_status || '',
      notes: '',
      // Auto-fill from contact_person (Tesis Yetkilisi)
      contacted_person: customer.contacted_person || customer.contact_person || '',
      contacted_person_title: customer.contacted_person_title || customer.contact_title || ''
    });
    setEditSalesDialogOpen(true);
  };

  const handleSaveEditSales = async () => {
    if (!selectedCustomer) return;
    setSaving(true);
    try {
      await axios.put(`${API}/sales/customers/${selectedCustomer.customer_id}`, {
        status: editSalesForm.status,
        amount: editSalesForm.amount ? parseFloat(editSalesForm.amount) : null,
        payment_status: editSalesForm.payment_status || null,
        notes: editSalesForm.notes || null,
        contacted_person: editSalesForm.contacted_person || null,
        contacted_person_title: editSalesForm.contacted_person_title || null
      }, { withCredentials: true });
      toast.success('Bilgiler güncellendi');
      setEditSalesDialogOpen(false);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  // Log Dialog
  const handleOpenLogDialog = async (customer) => {
    setSelectedCustomer(customer);
    setLogsLoading(true);
    setLogDialogOpen(true);
    try {
      const response = await axios.get(`${API}/sales/logs/${customer.customer_id}`, { withCredentials: true });
      setCustomerLogs(response.data);
    } catch (error) {
      toast.error('Loglar yüklenemedi');
    } finally {
      setLogsLoading(false);
    }
  };

  // Assign Sales Rep
  const handleAssignRep = async (customerId, repId) => {
    try {
      await axios.post(`${API}/sales/assign`, { customer_id: customerId, rep_id: repId }, { withCredentials: true });
      toast.success('Temsilci atandı');
      fetchCustomers();
    } catch (error) {
      toast.error('Atama başarısız');
    }
  };

  // Filter customers
  const filteredCustomers = customers.filter(customer => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      customer.name?.toLowerCase().includes(searchLower) ||
      customer.brand_name?.toLowerCase().includes(searchLower) ||
      customer.province?.toLowerCase().includes(searchLower) ||
      customer.city?.toLowerCase().includes(searchLower) ||
      customer.contact_person?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower) ||
      customer.safe_tourism_id?.toLowerCase().includes(searchLower);
    
    const matchesPhase = phaseFilter === "all" || customer.audit_phase === phaseFilter;
    const matchesStatus = statusFilter === "all" || customer.sales_status === statusFilter;
    const matchesAssign = assignFilter === "all" || customer.assigned_sales_rep_id === assignFilter;
    
    return matchesSearch && matchesPhase && matchesStatus && matchesAssign;
  });

  // Get status badge
  const getStatusBadge = (status) => {
    const config = ACTION_TYPES[status];
    if (!config) return <Badge variant="outline">-</Badge>;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  // Get payment badge
  const getPaymentBadge = (status) => {
    const config = PAYMENT_STATUS[status];
    if (!config) return null;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Müşteriler</h1>
          <p className="text-sm text-gray-500">Müşteri ve satış yönetimi</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={fetchCustomers}>
            <RefreshCw className="w-4 h-4 mr-1" /> Yenile
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Şablon
          </Button>
          <Button variant="outline" size="sm" onClick={() => setImportDialogOpen(true)}>
            <Upload className="w-4 h-4 mr-1" /> İçe Aktar
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="w-4 h-4 mr-1" /> Dışa Aktar
          </Button>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-[#1A4D2E] hover:bg-[#2A5D3E]">
            <Plus className="w-4 h-4 mr-1" /> Yeni Müşteri
          </Button>
        </div>
      </div>

      {/* Stats Cards - Colorful Design */}
      {stats && (
        <div className="mb-6">
          {/* First Row - Sales Pipeline */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-3">
            <Card className={`cursor-pointer transition-all hover:scale-105 border-l-4 border-gray-400 ${statusFilter === 'ilk_temas' ? 'ring-2 ring-gray-400' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'ilk_temas' ? 'all' : 'ilk_temas')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Phone className="w-8 h-8 text-gray-400" />
                  <span className="text-2xl font-bold text-gray-700">{stats.by_status?.ilk_temas?.count || 0}</span>
                </div>
                <div className="text-xs font-medium text-gray-600 mt-2">İlk Temas</div>
              </CardContent>
            </Card>
            
            <Card className={`cursor-pointer transition-all hover:scale-105 border-l-4 border-cyan-400 ${statusFilter === 'arama_yapildi' ? 'ring-2 ring-cyan-400' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'arama_yapildi' ? 'all' : 'arama_yapildi')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Phone className="w-8 h-8 text-cyan-500" />
                  <span className="text-2xl font-bold text-cyan-700">{stats.by_status?.arama_yapildi?.count || 0}</span>
                </div>
                <div className="text-xs font-medium text-cyan-600 mt-2">Arama Yapıldı</div>
              </CardContent>
            </Card>
            
            <Card className={`cursor-pointer transition-all hover:scale-105 border-l-4 border-blue-400 ${statusFilter === 'teklif_gonderildi' ? 'ring-2 ring-blue-400' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'teklif_gonderildi' ? 'all' : 'teklif_gonderildi')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Send className="w-8 h-8 text-blue-500" />
                  <span className="text-2xl font-bold text-blue-700">{stats.by_status?.teklif_gonderildi?.count || 0}</span>
                </div>
                <div className="text-xs font-medium text-blue-600 mt-2">Teklif Gönderildi</div>
              </CardContent>
            </Card>
            
            <Card className={`cursor-pointer transition-all hover:scale-105 border-l-4 border-purple-400 ${statusFilter === 'sozlesme_gonderildi' ? 'ring-2 ring-purple-400' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'sozlesme_gonderildi' ? 'all' : 'sozlesme_gonderildi')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <FileText className="w-8 h-8 text-purple-500" />
                  <span className="text-2xl font-bold text-purple-700">{stats.by_status?.sozlesme_gonderildi?.count || 0}</span>
                </div>
                <div className="text-xs font-medium text-purple-600 mt-2">Sözleşme Gönderildi</div>
              </CardContent>
            </Card>
            
            <Card className={`cursor-pointer transition-all hover:scale-105 border-l-4 border-green-500 ${statusFilter === 'sozlesme_imzalandi' ? 'ring-2 ring-green-500' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'sozlesme_imzalandi' ? 'all' : 'sozlesme_imzalandi')}>
              <CardContent className="p-4 bg-green-50">
                <div className="flex items-center justify-between">
                  <FileSignature className="w-8 h-8 text-green-600" />
                  <span className="text-2xl font-bold text-green-700">{stats.by_status?.sozlesme_imzalandi?.count || 0}</span>
                </div>
                <div className="text-xs font-medium text-green-700 mt-2">Sözleşme İmzalandı</div>
              </CardContent>
            </Card>
            
            <Card className={`cursor-pointer transition-all hover:scale-105 border-l-4 border-yellow-400 ${statusFilter === 'beklemede' ? 'ring-2 ring-yellow-400' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'beklemede' ? 'all' : 'beklemede')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Clock className="w-8 h-8 text-yellow-500" />
                  <span className="text-2xl font-bold text-yellow-700">{stats.by_status?.beklemede?.count || 0}</span>
                </div>
                <div className="text-xs font-medium text-yellow-600 mt-2">Beklemede</div>
              </CardContent>
            </Card>
            
            <Card className={`cursor-pointer transition-all hover:scale-105 border-l-4 border-red-400 ${statusFilter === 'reddedildi' ? 'ring-2 ring-red-400' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'reddedildi' ? 'all' : 'reddedildi')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <XCircle className="w-8 h-8 text-red-500" />
                  <span className="text-2xl font-bold text-red-700">{stats.by_status?.reddedildi?.count || 0}</span>
                </div>
                <div className="text-xs font-medium text-red-600 mt-2">Reddedildi</div>
              </CardContent>
            </Card>
          </div>
          
          {/* Second Row - Audit Progress & Financials */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card className={`cursor-pointer transition-all hover:scale-105 border-l-4 border-indigo-500 ${statusFilter === 'planlandi' ? 'ring-2 ring-indigo-500' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'planlandi' ? 'all' : 'planlandi')}>
              <CardContent className="p-4 bg-indigo-50">
                <div className="flex items-center justify-between">
                  <Calendar className="w-8 h-8 text-indigo-600" />
                  <span className="text-2xl font-bold text-indigo-700">{stats.by_status?.planlandi?.count || 0}</span>
                </div>
                <div className="text-xs font-medium text-indigo-700 mt-2">Planlandı</div>
              </CardContent>
            </Card>
            
            <Card className={`cursor-pointer transition-all hover:scale-105 border-l-4 border-emerald-500 ${statusFilter === 'denetim_tamamlandi' ? 'ring-2 ring-emerald-500' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'denetim_tamamlandi' ? 'all' : 'denetim_tamamlandi')}>
              <CardContent className="p-4 bg-emerald-50">
                <div className="flex items-center justify-between">
                  <ClipboardCheck className="w-8 h-8 text-emerald-600" />
                  <span className="text-2xl font-bold text-emerald-700">{stats.by_status?.denetim_tamamlandi?.count || 0}</span>
                </div>
                <div className="text-xs font-medium text-emerald-700 mt-2">Denetim Tamamlandı</div>
              </CardContent>
            </Card>
            
            <Card className={`cursor-pointer transition-all hover:scale-105 border-l-4 border-teal-500 ${statusFilter === 'tamamlandi' ? 'ring-2 ring-teal-500' : ''}`}
              onClick={() => setStatusFilter(statusFilter === 'tamamlandi' ? 'all' : 'tamamlandi')}>
              <CardContent className="p-4 bg-teal-50">
                <div className="flex items-center justify-between">
                  <CheckCircle className="w-8 h-8 text-teal-600" />
                  <span className="text-2xl font-bold text-teal-700">{stats.by_status?.tamamlandi?.count || 0}</span>
                </div>
                <div className="text-xs font-medium text-teal-700 mt-2">Tamamlandı</div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Ara (Ad, Marka, Şehir, Yetkili, Email, TGA ID)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={phaseFilter} onValueChange={setPhaseFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Aşama" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Aşamalar</SelectItem>
                <SelectItem value="2">2. Aşama</SelectItem>
                <SelectItem value="3">3. Aşama</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Durum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Durumlar</SelectItem>
                {Object.entries(ACTION_TYPES).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isAdmin && salesReps.length > 0 && (
              <Select value={assignFilter} onValueChange={setAssignFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Temsilci" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Temsilciler</SelectItem>
                  {salesReps.map(rep => (
                    <SelectItem key={rep.user_id} value={rep.user_id}>{rep.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {(statusFilter !== 'all' || phaseFilter !== 'all' || assignFilter !== 'all') && (
              <Button variant="ghost" size="sm" onClick={() => { setStatusFilter('all'); setPhaseFilter('all'); setAssignFilter('all'); }}>
                <X className="w-4 h-4 mr-1" /> Temizle
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Müşteri</TableHead>
                  <TableHead>İl / İlçe</TableHead>
                  <TableHead>İletişim</TableHead>
                  <TableHead>Aşama</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Tutar</TableHead>
                  <TableHead>Ödeme</TableHead>
                  {isAdmin && <TableHead>Temsilci</TableHead>}
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 9 : 8} className="text-center py-8 text-gray-500">
                      Müşteri bulunamadı
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCustomers.map((customer) => (
                    <TableRow key={customer.customer_id} className="hover:bg-gray-50">
                      <TableCell>
                        <div className="font-medium text-gray-900">{customer.name}</div>
                        {customer.brand_name && (
                          <div className="text-xs text-gray-500">{customer.brand_name}</div>
                        )}
                        {customer.safe_tourism_id && (
                          <div className="text-xs text-blue-600">TGA: {customer.safe_tourism_id}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{customer.province}</div>
                        <div className="text-xs text-gray-500">{customer.city}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{customer.contact_person}</div>
                        <div className="text-xs text-gray-500">{customer.phone}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{customer.audit_phase}. Aşama</Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(customer.sales_status)}</TableCell>
                      <TableCell>
                        {customer.sales_amount ? (
                          <div>
                            <div className="font-medium">{formatCurrency(customer.sales_amount)}</div>
                            <div className="text-xs text-gray-500">
                              KDV: {formatCurrency(customer.sales_amount * 1.20)}
                            </div>
                          </div>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{getPaymentBadge(customer.payment_status) || '-'}</TableCell>
                      {isAdmin && (
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 text-xs">
                                {customer.assigned_sales_rep_name || 'Atanmamış'}
                                <ChevronDown className="w-3 h-3 ml-1" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              {salesReps.map(rep => (
                                <DropdownMenuItem key={rep.user_id} onClick={() => handleAssignRep(customer.customer_id, rep.user_id)}>
                                  {rep.name}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleEdit(customer)} title="Düzenle">
                            <Pencil className="w-4 h-4 text-gray-600" />
                          </Button>
                          {canManageSales && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenEditSalesDialog(customer)} title="Satış Bilgisi">
                              <DollarSign className="w-4 h-4 text-amber-600" />
                            </Button>
                          )}
                          {canManageSales && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenActionDialog(customer)} title="Aksiyon Ekle">
                              <Plus className="w-4 h-4 text-emerald-600" />
                            </Button>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleOpenLogDialog(customer)} title="Geçmiş">
                            <History className="w-4 h-4 text-blue-600" />
                          </Button>
                          {canPlanAudit && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/audits/new?customer=${customer.customer_id}`)} title="Denetim Planla">
                              <CalendarDays className="w-4 h-4 text-purple-600" />
                            </Button>
                          )}
                          {isAdmin && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(customer.customer_id)} title="Sil">
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Customer Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCustomer ? "Müşteri Düzenle" : "Yeni Müşteri"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="basic">Temel</TabsTrigger>
                <TabsTrigger value="contact">İletişim</TabsTrigger>
                <TabsTrigger value="facility">Tesis</TabsTrigger>
                <TabsTrigger value="certificate">Sertifika</TabsTrigger>
              </TabsList>
              
              <TabsContent value="basic" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Ticari Unvan *</Label>
                    <Input value={formData.name} onChange={(e) => {
                      const newName = e.target.value;
                      if (!formData.company_name) {
                        setFormData({ ...formData, name: newName, company_name: newName });
                      } else {
                        setFormData({ ...formData, name: newName });
                      }
                    }} required />
                  </div>
                  <div>
                    <Label>Marka Adı (Tesis Adı)</Label>
                    <Input value={formData.brand_name} onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })} placeholder="Otomatik doldurulur veya manuel girin" />
                  </div>
                  <div>
                    <Label>Denetim Firma İsmi</Label>
                    <Input value={formData.company_name} onChange={(e) => setFormData({ ...formData, company_name: e.target.value })} placeholder="Denetimi yapan firmanın adı" />
                  </div>
                  <div>
                    <Label>TGA Tesis ID</Label>
                    <Input value={formData.safe_tourism_id} onChange={(e) => setFormData({ ...formData, safe_tourism_id: e.target.value })} />
                  </div>
                  <div>
                    <Label>Vergi No</Label>
                    <Input value={formData.tax_number} onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })} />
                  </div>
                  <div>
                    <Label>Kaynak</Label>
                    <Input value={formData.source} onChange={(e) => setFormData({ ...formData, source: e.target.value })} placeholder="Referans kaynağı" />
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="contact" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Yetkili Kişi</Label>
                    <Input value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} />
                  </div>
                  <div>
                    <Label>Yetkili Ünvanı</Label>
                    <Input value={formData.contact_title} onChange={(e) => setFormData({ ...formData, contact_title: e.target.value })} />
                  </div>
                  <div>
                    <Label>Yetkili Email (Bildirimler için)</Label>
                    <Input type="email" value={formData.contact_person_email} onChange={(e) => setFormData({ ...formData, contact_person_email: e.target.value })} placeholder="Denetim bildirimleri bu adrese gönderilir" />
                  </div>
                  <div>
                    <Label>Tesis Email</Label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                  </div>
                  <div>
                    <Label>Telefon</Label>
                    <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                  </div>
                  <div>
                    <Label>İl</Label>
                    <Input value={formData.province} onChange={(e) => setFormData({ ...formData, province: e.target.value })} />
                  </div>
                  <div>
                    <Label>İlçe</Label>
                    <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
                  </div>
                  <div className="col-span-2">
                    <Label>Adres</Label>
                    <Textarea value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="facility" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tesis Adı</Label>
                    <Input value={formData.facility_name} onChange={(e) => {
                      const newFacilityName = e.target.value;
                      // Marka Adı boşsa otomatik doldur
                      if (!formData.brand_name) {
                        setFormData({ ...formData, facility_name: newFacilityName, brand_name: newFacilityName });
                      } else {
                        setFormData({ ...formData, facility_name: newFacilityName });
                      }
                    }} />
                  </div>
                  <div>
                    <Label>Tesis Türü</Label>
                    <Select value={formData.facility_type} onValueChange={(v) => setFormData({ ...formData, facility_type: v })}>
                      <SelectTrigger><SelectValue placeholder="Seçin" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="otel">Otel</SelectItem>
                        <SelectItem value="tatil_koyu">Tatil Köyü</SelectItem>
                        <SelectItem value="butik_otel">Butik Otel</SelectItem>
                        <SelectItem value="apart_otel">Apart Otel</SelectItem>
                        <SelectItem value="pansiyon">Pansiyon</SelectItem>
                        <SelectItem value="hostel">Hostel</SelectItem>
                        <SelectItem value="tur_operatoru">Tur Operatörü</SelectItem>
                        <SelectItem value="seyahat_acentasi">Seyahat Acentası</SelectItem>
                        <SelectItem value="ozel_konaklama">Özel Konaklama</SelectItem>
                        <SelectItem value="termal_otel">Termal Otel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Yıldız/Sınıf</Label>
                    <Input value={formData.star_rating} onChange={(e) => setFormData({ ...formData, star_rating: e.target.value })} placeholder="5 Yıldızlı" />
                  </div>
                  <div>
                    <Label>Bakanlık Belge Türü</Label>
                    <Select value={formData.bakanlik_belge_turu} onValueChange={(v) => setFormData({ ...formData, bakanlik_belge_turu: v })}>
                      <SelectTrigger><SelectValue placeholder="Seçin" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="basit_konaklama">Basit Konaklama</SelectItem>
                        <SelectItem value="turizm_isletme">Turizm İşletme Belgesi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Oda Sayısı</Label>
                    <Input type="number" value={formData.room_count} onChange={(e) => setFormData({ ...formData, room_count: e.target.value })} />
                  </div>
                  <div>
                    <Label>Yatak Sayısı</Label>
                    <Input type="number" value={formData.bed_count} onChange={(e) => setFormData({ ...formData, bed_count: e.target.value })} />
                  </div>
                  <div>
                    <Label>Çalışan Sayısı</Label>
                    <Input type="number" value={formData.employee_count} onChange={(e) => setFormData({ ...formData, employee_count: e.target.value })} />
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="certificate" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Denetim Aşaması</Label>
                    <Select value={formData.audit_phase} onValueChange={(v) => setFormData({ ...formData, audit_phase: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2">2. Aşama</SelectItem>
                        <SelectItem value="3">3. Aşama</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Denetim Türü</Label>
                    <Select value={formData.audit_type} onValueChange={(v) => setFormData({ ...formData, audit_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Initial Certification">İlk Belgelendirme</SelectItem>
                        <SelectItem value="Surveillance 1">Gözetim 1</SelectItem>
                        <SelectItem value="Surveillance 2">Gözetim 2</SelectItem>
                        <SelectItem value="Re-Certification">Yeniden Belgelendirme</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Sertifika No</Label>
                    <Input value={formData.certificate_number} onChange={(e) => setFormData({ ...formData, certificate_number: e.target.value })} />
                  </div>
                  <div>
                    <Label>Sertifika Durumu</Label>
                    <Input value={formData.certificate_status} onChange={(e) => setFormData({ ...formData, certificate_status: e.target.value })} />
                  </div>
                  <div>
                    <Label>Düzenlenme Tarihi</Label>
                    <Input type="date" value={formData.certificate_issue_date} onChange={(e) => setFormData({ ...formData, certificate_issue_date: e.target.value })} />
                  </div>
                  <div>
                    <Label>Geçerlilik Tarihi</Label>
                    <Input type="date" value={formData.certificate_validity_date} onChange={(e) => setFormData({ ...formData, certificate_validity_date: e.target.value })} />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>İptal</Button>
              <Button type="submit" className="bg-[#1A4D2E] hover:bg-[#2A5D3E]">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Satış Aksiyonu Ekle</DialogTitle>
            <DialogDescription>{selectedCustomer?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Aksiyon *</Label>
              <Select value={actionForm.action_type} onValueChange={(v) => setActionForm({ ...actionForm, action_type: v })}>
                <SelectTrigger><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(MANUAL_ACTION_TYPES).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tutar (₺)</Label>
                <Input type="number" value={actionForm.amount} onChange={(e) => setActionForm({ ...actionForm, amount: e.target.value })} />
              </div>
              <div>
                <Label>Ödeme Durumu</Label>
                <Select value={actionForm.payment_status} onValueChange={(v) => setActionForm({ ...actionForm, payment_status: v })}>
                  <SelectTrigger><SelectValue placeholder="Seçin" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PAYMENT_STATUS).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Görüşülen Kişi</Label>
                <Input value={actionForm.contact_person} onChange={(e) => setActionForm({ ...actionForm, contact_person: e.target.value })} />
              </div>
              <div>
                <Label>Ünvan</Label>
                <Input value={actionForm.contact_title} onChange={(e) => setActionForm({ ...actionForm, contact_title: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Ödeme Sözü Tarihi</Label>
              <Input type="date" value={actionForm.payment_promise_date} onChange={(e) => setActionForm({ ...actionForm, payment_promise_date: e.target.value })} />
            </div>
            <div>
              <Label>Notlar</Label>
              <Textarea value={actionForm.notes} onChange={(e) => setActionForm({ ...actionForm, notes: e.target.value })} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialogOpen(false)}>İptal</Button>
            <Button onClick={handleSubmitAction} className="bg-[#1A4D2E] hover:bg-[#2A5D3E]">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Sales Dialog */}
      <Dialog open={editSalesDialogOpen} onOpenChange={setEditSalesDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Satış Bilgilerini Düzenle</DialogTitle>
            <DialogDescription>{selectedCustomer?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Durum</Label>
              <Select value={editSalesForm.status} onValueChange={(v) => setEditSalesForm({ ...editSalesForm, status: v })}>
                <SelectTrigger><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ACTION_TYPES).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tutar (₺)</Label>
              <Input type="number" value={editSalesForm.amount} onChange={(e) => setEditSalesForm({ ...editSalesForm, amount: e.target.value })} />
              {editSalesForm.amount && (
                <p className="text-xs text-gray-500 mt-1">KDV Dahil: {formatCurrency(parseFloat(editSalesForm.amount) * 1.20)}</p>
              )}
            </div>
            <div>
              <Label>Ödeme Durumu</Label>
              <Select value={editSalesForm.payment_status} onValueChange={(v) => setEditSalesForm({ ...editSalesForm, payment_status: v })}>
                <SelectTrigger><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PAYMENT_STATUS).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Görüşülen Kişi</Label>
                <Input 
                  value={editSalesForm.contacted_person} 
                  onChange={(e) => setEditSalesForm({ ...editSalesForm, contacted_person: e.target.value })} 
                  placeholder="Ad Soyad"
                />
              </div>
              <div>
                <Label>Ünvanı</Label>
                <Input 
                  value={editSalesForm.contacted_person_title} 
                  onChange={(e) => setEditSalesForm({ ...editSalesForm, contacted_person_title: e.target.value })} 
                  placeholder="Ünvan"
                />
              </div>
            </div>
            <div>
              <Label>Not</Label>
              <Textarea value={editSalesForm.notes} onChange={(e) => setEditSalesForm({ ...editSalesForm, notes: e.target.value })} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSalesDialogOpen(false)}>İptal</Button>
            <Button onClick={handleSaveEditSales} disabled={saving} className="bg-[#1A4D2E] hover:bg-[#2A5D3E]">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Kaydediliyor...</> : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Log Dialog */}
      <Dialog open={logDialogOpen} onOpenChange={setLogDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>İşlem Geçmişi</DialogTitle>
            <DialogDescription>{selectedCustomer?.name}</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh]">
            {logsLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : customerLogs.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Henüz işlem kaydı yok</p>
            ) : (
              <div className="space-y-3">
                {customerLogs.map((log, index) => (
                  <div key={index} className="border rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="font-medium">{log.action}</span>
                        <span className="text-gray-500 text-sm ml-2">- {log.user_name}</span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {new Date(log.created_at).toLocaleString('tr-TR')}
                      </span>
                    </div>
                    {log.details && <p className="text-sm text-gray-600 mt-1">{log.details}</p>}
                    {log.new_value && <Badge className="mt-1" variant="outline">{log.new_value}</Badge>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Müşteri İçe Aktar</DialogTitle>
            <DialogDescription>Excel dosyası yükleyin</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleImport}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#1A4D2E] file:text-white hover:file:bg-[#2A5D3E]"
              disabled={importing}
            />
            {importing && (
              <div className="flex items-center gap-2 mt-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>İçe aktarılıyor...</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
